import React, { useState, useEffect } from "react";
import { FaEdit, FaTimes, FaUsers, FaFileAlt, FaCalendarAlt, FaCheckCircle, FaClock, FaTimesCircle, FaTags, FaInfoCircle, FaTrash, FaLock, FaLockOpen, FaUniversity } from "react-icons/fa";
import api from "../utils/api";

const CAMPUS_OPTIONS = ["Isulan", "ACCESS", "Tacurong", "Bagumbayan", "Kalamansig", "Lutayan", "Palimbang"];

function getSessionUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export default function EditResearchModal({ isOpen, onClose, research, onResearchUpdated }) {
  const [formData, setFormData] = useState({
    title: "",
    authors: "",
    abstract: "",
    year: "",
    keywords: "",
    status: "pending",
    isPublic: "false",
    campus: "Isulan"
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (research) {
      setFormData({
        title: research.title || "",
        authors: Array.isArray(research.authors) ? research.authors.join(", ") : research.authors || "",
        abstract: research.abstract || "",
        year: research.year || "",
        keywords: Array.isArray(research.keywords) ? research.keywords.join(", ") : research.keywords || "",
        status: research.status || "pending",
        isPublic: research.isPublic ? "true" : "false",
        campus: research.campus && CAMPUS_OPTIONS.includes(research.campus) ? research.campus : CAMPUS_OPTIONS[0]
      });
    }
  }, [research]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const updateData = {
        title: formData.title,
        abstract: formData.abstract,
        year: formData.year,
        status: formData.status,
        isPublic: formData.isPublic,
        authors: formData.authors.split(",").map(author => author.trim()).filter(author => author),
        keywords: formData.keywords.split(",").map(keyword => keyword.trim()).filter(keyword => keyword)
      };

      if (getSessionUser()?.role === "admin") {
        updateData.campus = formData.campus;
      }

      await api.put(`/research/${research._id}`, updateData);
      onResearchUpdated();
      onClose();
    } catch (error) {
      console.error("Error updating research:", error);
      setError(error.response?.data?.error || error.response?.data?.message || "Failed to update research");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!research?._id) return;
    if (!window.confirm(`Delete this research and its uploaded file?\n\n"${research.title}"`)) return;

    setDeleting(true);
    setError("");
    try {
      await api.delete(`/research/${research._id}`);
      onResearchUpdated();
      onClose();
    } catch (err) {
      console.error("Error deleting research:", err);
      setError(err.response?.data?.error || "Failed to delete research");
    } finally {
      setDeleting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="text-green-400" />;
      case 'pending':
        return <FaClock className="text-yellow-400" />;
      case 'rejected':
        return <FaTimesCircle className="text-red-400" />;
      default:
        return <FaClock className="text-gray-400" />;
    }
  };

  if (!isOpen || !research) return null;

  const isAdmin = getSessionUser()?.role === "admin";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="relative bg-gray-900 p-8 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white text-2xl font-bold focus:outline-none group"
          aria-label="Close"
        >
          <FaTimes className="group-hover:scale-110 transition-transform" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FaEdit className="text-blue-500 text-2xl" />
          <h2 className="text-2xl font-bold text-white">Edit Research</h2>
        </div>

        {error && (
          <div className="bg-red-600 text-white p-4 rounded-lg border border-red-500 mb-6">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-6 p-4 rounded-xl bg-gray-800/80 border border-gray-600">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-400">Visibility</span>
            {formData.isPublic === "true" ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-emerald-900/80 text-emerald-200 border border-emerald-700">
                <FaLockOpen className="text-emerald-400" /> Public
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-slate-700 text-slate-200 border border-slate-500">
                <FaLock className="text-slate-400" /> Private
              </span>
            )}
          </div>
          <div className="w-px h-6 bg-gray-600 hidden sm:block" aria-hidden />
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-400">Campus</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-indigo-900/80 text-indigo-200 border border-indigo-700">
              <FaUniversity className="text-indigo-400 shrink-0" />
              {formData.campus}
            </span>
          </div>
          <div className="w-px h-6 bg-gray-600 hidden sm:block" aria-hidden />
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-400">Approval</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
              formData.status === "approved" ? "bg-green-900/80 text-green-200 border border-green-700" :
              formData.status === "rejected" ? "bg-red-900/80 text-red-200 border border-red-700" :
              "bg-amber-900/80 text-amber-200 border border-amber-700"
            }`}>
              {getStatusIcon(formData.status)}
              <span className="capitalize">{formData.status}</span>
            </span>
          </div>
          {research.pdfFilename && (
            <>
              <div className="w-full sm:w-auto sm:ml-auto text-sm text-gray-400">
                <span className="text-gray-500">Uploaded file: </span>
                <span className="text-gray-200 font-mono text-xs break-all">{research.pdfFilename}</span>
              </div>
            </>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaFileAlt className="text-blue-400" />
              Title *
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Enter the complete research title
                </div>
              </div>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaUsers className="text-blue-400" />
              Authors * (comma-separated)
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Separate multiple authors with commas
                </div>
              </div>
            </label>
            <input
              type="text"
              name="authors"
              value={formData.authors}
              onChange={handleChange}
              required
              placeholder="Author 1, Author 2, Author 3"
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaFileAlt className="text-blue-400" />
              Abstract *
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Brief summary of the research
                </div>
              </div>
            </label>
            <textarea
              name="abstract"
              value={formData.abstract}
              onChange={handleChange}
              required
              rows="4"
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <FaCalendarAlt className="text-blue-400" />
                Year *
                <div className="relative group">
                  <FaInfoCircle className="text-gray-500 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    Year the research was completed
                  </div>
                </div>
              </label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                min="1900"
                max={new Date().getFullYear() + 1}
                className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                Approval status
                <div className="relative group">
                  <FaInfoCircle className="text-gray-500 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    Whether this research is approved for the catalog
                  </div>
                </div>
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all appearance-none"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  {getStatusIcon(formData.status)}
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaLockOpen className="text-blue-400" />
              Visibility (public or private)
            </label>
            <select
              name="isPublic"
              value={formData.isPublic}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
            >
              <option value="true">Public — listed when approved</option>
              <option value="false">Private — access by request only</option>
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaUniversity className="text-blue-400" />
              Campus *
              {!isAdmin && (
                <span className="text-xs font-normal text-gray-500">(only an administrator can change this)</span>
              )}
            </label>
            {isAdmin ? (
              <select
                name="campus"
                value={formData.campus}
                onChange={handleChange}
                required
                className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              >
                {CAMPUS_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                readOnly
                value={formData.campus}
                className="w-full p-3 rounded-lg bg-gray-800/60 text-gray-400 border border-gray-600 cursor-not-allowed"
              />
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaTags className="text-blue-400" />
              Keywords (comma-separated)
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Separate keywords with commas
                </div>
              </div>
            </label>
            <input
              type="text"
              name="keywords"
              value={formData.keywords}
              onChange={handleChange}
              placeholder="keyword1, keyword2, keyword3"
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
            />
          </div>

          <div className="flex flex-wrap justify-between items-center gap-3 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={handleDelete}
              disabled={loading || deleting}
              className="bg-red-900/80 hover:bg-red-800 disabled:opacity-50 px-4 py-3 rounded-lg text-red-100 font-medium border border-red-700 flex items-center gap-2"
            >
              {deleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-100" />
                  Deleting...
                </>
              ) : (
                <>
                  <FaTrash />
                  Delete uploaded research
                </>
              )}
            </button>
            <div className="flex gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 flex items-center gap-2"
              >
                <FaTimes />
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || deleting}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <FaEdit />
                    Save changes
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
} 